import React, { useEffect, useRef, useState } from 'react';
import Layout from '../../components/Layout/Layout';
import { callApi } from '../../api/callApi';
import { API_ENDPOINT, API_URL_2 } from '../../api/constant';
import { useForm, useWatch } from 'react-hook-form';
import { showToastNotification } from '../../utility/helper';
import InputField from '../../components/UI/Forms/InputField';
import ErrorMessage from '../../components/UI/Notifications/ErrorMessage';
import Button from '../../components/UI/Buttons/Button';
import useImageUpload from '../../hooks/useImageUpload';
import useFileDelete from '../../hooks/useFileDelete';
import { useNavigate, useParams } from 'react-router-dom';
import SeoForm, { SeoFormRef, submitSEOData } from '../../components/common/SEOForm';
import { SeoFormData } from '../../interfaces/seo';
import { IProduct } from '../../interfaces/product'; // adjust path if needed

interface SelectedProduct {
    product: IProduct;
    selected: boolean;
    quantity: number;
    price: string;
}

const AddMeateryBox: React.FC = () => {
    const { uploadImage, error: uploadError } = useImageUpload();
    const { deleteFile } = useFileDelete();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [loading, setLoading] = useState<boolean>(true);
    const [loadingButton, setLoadingButton] = useState<boolean>(false);

    const { register, handleSubmit, formState: { errors }, setValue, control } = useForm({ mode: 'onBlur' });
    const titleValue = useWatch({ control, name: 'title' });

    const [isSlugTouched, setIsSlugTouched] = useState(false);
    const [imageURL, setImageURL] = useState<string>('');
    const [editingBox, setEditingBox] = useState<any | null>(null);
    const seoFormRef = useRef<SeoFormRef>(null);
    const [seoData, setSeoData] = useState<SeoFormData>({ entityType: 'bundle' });

    // products
    const [products, setProducts] = useState<IProduct[]>([]);
    const [filteredProducts, setFilteredProducts] = useState<IProduct[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedProducts, setSelectedProducts] = useState<Record<string, SelectedProduct>>({});
    const [selectAll, setSelectAll] = useState<boolean>(false);

    useEffect(() => {
        if (titleValue && !isSlugTouched) {
            const generatedSlug = generateSlug(titleValue);
            setValue('slug', generatedSlug);
        }
    }, [titleValue, isSlugTouched, setValue]);

    const generateSlug = (text: string) =>
        text.toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9-]/g, '')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');

    const handleSlugFocus = () => setIsSlugTouched(true);

    // FETCH PRODUCTS + BOX (parallel-ish)
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                await fetchProducts(''); // this sets `products`
                if (id) {
                    await getMeateryBoxById(id); // this sets `selectedProducts` (may contain partial product objects)
                }
            } catch (err: any) {
                console.error(err);
                showToastNotification('danger', 'Failed to load data');
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // debounce search fetch
    useEffect(() => {
        if (searchTerm.trim().length < 3) {
            // If search term is short, just re-run local filtering (no remote fetch)
            // The reconciliation & ordering useEffect below will run because `searchTerm` is a dependency.
            return;
        }

        const handler = setTimeout(() => {
            fetchProducts(searchTerm);
        }, 400);

        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchProducts = async (query: string) => {
        try {
            const url = query
                ? `${API_URL_2}${API_ENDPOINT.page}products?title=${encodeURIComponent(query)}`
                : `${API_URL_2}${API_ENDPOINT.page}products`;

            const res = await callApi(url, { method: 'GET' });
            const list: IProduct[] = Array.isArray(res) ? res : (res?.data || res?.products || []);
            setProducts(list);
            // Do NOT set filteredProducts here — let the reconciliation/useEffect handle ordering
        } catch (err) {
            console.error('fetchProducts error', err);
            setProducts([]);
            setFilteredProducts([]);
        }
    };

    const [initialProductsLoaded, setInitialProductsLoaded] = useState(false);

    const getMeateryBoxById = async (boxId: string) => {
        try {
            const res = await callApi(API_URL_2 + API_ENDPOINT.page + `bundles/${boxId}`, { method: 'GET' });
            setEditingBox(res);

            // Build selected map only from items[] (items.product may be partial)
            const selectedMap = buildSelectedFromItems(res.items || []);
            setSelectedProducts(selectedMap);

            // Form values
            setValue('title', res.title);
            setValue('slug', res.slug);
            setValue('description', res.description);
            setValue('additionalItems', res.additionalItems);
            setValue('price', res.price);
            setValue('isActive', res.isActive);
            setImageURL(res.images?.[0] ?? '');

            if (res.slug) setIsSlugTouched(true);
            if (res.seo) setSeoData(res.seo);

            // Mark that we have initial selected products loaded
            setInitialProductsLoaded(true);
        } catch (err) {
            console.error(err);
            showToastNotification('danger', 'Failed to load meatery box');
        }
    };


    // SINGLE authoritative reconciliation + ordering effect:
    useEffect(() => {
        // Skip if we're still loading initial data
        if (id && !initialProductsLoaded && Object.keys(selectedProducts).length === 0) {
            return;
        }

        // 1) Reconcile partial selectedProducts.product with full product objects from `products`
        let changed = false;
        const reconciled = { ...selectedProducts };

        // Only reconcile if we have products to work with
        if (products.length > 0) {
            for (const pid of Object.keys(reconciled)) {
                const full = products.find(p => p.id === pid);
                if (full && (!reconciled[pid].product || reconciled[pid].product.id !== full.id)) {
                    reconciled[pid] = { ...reconciled[pid], product: full };
                    changed = true;
                }
            }

            // Also check if we have any selected products that aren't in the current products list
            // This can happen if a product was deleted or not returned in the search
            for (const pid of Object.keys(reconciled)) {
                if (!products.find(p => p.id === pid)) {
                    // Product not found in current products list - keep it anyway for display
                    // This ensures selected products stay visible even if not in search results
                }
            }

            if (changed) {
                setSelectedProducts(reconciled);
            }
        }

        // Use the most recent map for ordering
        const selMap = changed ? reconciled : selectedProducts;
        const selectedIds = new Set(Object.keys(selMap));

        // 2) Build base list depending on searchTerm
        const q = searchTerm.trim().toLowerCase();
        let base = products.slice();

        if (q.length >= 1) {
            base = base.filter(p =>
                (p.title || '').toLowerCase().includes(q) ||
                (p.productCode || '').toLowerCase().includes(q)
            );
        }

        // 3) Special handling for editing mode: ensure selected products appear even if not in current products list
        if (id && Object.keys(selMap).length > 0) {
            // For each selected product, if it's not in the base array, add it
            for (const pid of selectedIds) {
                if (!base.find(p => p.id === pid)) {
                    const selectedItem = selMap[pid];
                    if (selectedItem && selectedItem.product) {
                        base.push(selectedItem.product);
                    }
                }
            }
        }

        // 4) Put selected on top (while preserving relative order from `products`)
        const selectedList: IProduct[] = [];
        const unselectedList: IProduct[] = [];

        for (const p of base) {
            if (selectedIds.has(p.id)) selectedList.push(p);
            else unselectedList.push(p);
        }

        setFilteredProducts([...selectedList, ...unselectedList]);
    }, [products, selectedProducts, searchTerm, id, initialProductsLoaded]);
    const onProductToggle = (product: IProduct, checked: boolean) => {
        setSelectedProducts(prev => {
            const copy = { ...prev };
            if (checked) {
                const defaultQty = product.productInfo?.[0]?.quantity ?? 1;
                const defaultPrice = product.productInfo?.[0]?.price ?? '0';
                copy[product.id] = { product, selected: true, quantity: defaultQty, price: defaultPrice };
            } else {
                delete copy[product.id];
            }
            return copy;
        });
    };

    const onSelectAllToggle = (checked: boolean) => {
        setSelectAll(checked);
        if (checked) {
            setSelectedProducts(prev => {
                const copy = { ...prev };
                for (const p of filteredProducts) {
                    if (!copy[p.id]) {
                        copy[p.id] = {
                            product: p,
                            selected: true,
                            quantity: p.productInfo?.[0]?.quantity ?? 1,
                            price: p.productInfo?.[0]?.price ?? '0',
                        };
                    } else {
                        copy[p.id].selected = true;
                    }
                }
                return copy;
            });
        } else {
            setSelectedProducts(prev => {
                const copy = { ...prev };
                for (const p of filteredProducts) {
                    delete copy[p.id];
                }
                return copy;
            });
        }
    };

    const onChangeSelectedQuantity = (productId: string, qty: number) => {
        setSelectedProducts(prev => {
            const copy = { ...prev };
            if (copy[productId]) copy[productId] = { ...copy[productId], quantity: qty };
            return copy;
        });
    };

    // const onChangeSelectedPrice = (productId: string, price: string) => {
    //     setSelectedProducts(prev => {
    //         const copy = { ...prev };
    //         if (copy[productId]) copy[productId] = { ...copy[productId], price };
    //         return copy;
    //     });
    // };

    const onSubmit = async (data: any) => {
        setLoadingButton(true);

        const productsPayload = Object.values(selectedProducts)
            .filter(s => s.selected)
            .map(s => ({
                productId: s.product.id,
                quantity: s.quantity,
                price: s.price,
            }));

        const postData = {
            images: [imageURL],
            title: data.title,
            slug: data.slug,
            isActive: data.isActive ?? true,
            description: data.description,
            additionalItems: data.additionalItems,
            price: data.price ?? null,
            items: productsPayload,
        };
        try {
            let boxId = editingBox ? editingBox.id : '';
            if (editingBox) {
                editingBox.slug && delete postData.slug; // do not send slug if editing and slug exists
                await callApi(API_URL_2 + API_ENDPOINT.page + `bundles/${editingBox.id}`, {
                    method: 'PATCH',
                    data: postData,
                });
                showToastNotification('success', 'Meatery box updated successfully');
                boxId = editingBox.id;
            } else {
                const res = await callApi(API_URL_2 + API_ENDPOINT.page + 'bundles', {
                    method: 'POST',
                    data: postData,
                });
                boxId = res?.id;
                showToastNotification('success', 'Meatery box created successfully');
            }

            if (seoFormRef.current) {
                const isValid = await seoFormRef.current.triggerValidation();
                const seoFormData = seoFormRef.current.getFormData();
                if (isValid) {
                    const newSeoData = {
                        ...seoFormData,
                        entityId: boxId,
                        entityType: 'bundle',
                    };
                    submitSEOData(newSeoData, seoData?.id);
                } else {
                    showToastNotification('danger', 'SEO form has validation errors');
                }
            }

            navigate('/meatery-boxes');
        } catch (err: any) {
            console.error(err);
            showToastNotification('danger', err?.message ?? 'Failed to save');
        } finally {
            setLoadingButton(false);
        }
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (file.size > 1 * 1024 * 1024) {
            return showToastNotification('danger', `"${file.name}" exceeds 1MB limit.`);
        }
        try {
            const uploadedUrl = await uploadImage(file);
            setImageURL(uploadedUrl);
        } catch (err) {
            console.error(err);
            showToastNotification('danger', 'Image upload failed');
        }
    };

    const handleDeleteImage = async (imageURLLocal: string) => {
        await deleteFile(imageURLLocal);
        setImageURL('');
    };

    const handleCancel = () => navigate('/meatery-boxes');

    const buildSelectedFromItems = (items: any[]) => {
        const map: Record<string, SelectedProduct> = {};

        for (const item of items) {
            const product = item.product;
            map[item.productId] = {
                product,
                selected: true,
                quantity: item.quantity ?? (product?.productInfo?.[0]?.quantity ?? 1),
                price:
                    item.priceOverride ??
                    product?.productInfo?.[0]?.price ??
                    '0',
            };
        }

        return map;
    };

    if (loading) {
        return (
            <Layout>
                <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </Layout>
        );
    }

    const headerText = editingBox ? 'Update Meatery Box' : 'Add Meatery Box';

    return (
        <Layout>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1 className="app-page-title">{headerText}</h1>
                <button className="btn btn-outline-secondary" onClick={handleCancel}>
                    <i className="bi bi-arrow-left me-2" /> Back to Meatery Boxes
                </button>
            </div>

            <div className="row">
                <div className="col-lg-10 mx-auto">
                    <div className="app-card app-card-settings shadow-sm p-4">
                        <div className="app-card-body">
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <div className="row">
                                    <div className="mb-3 col-12">
                                        <InputField
                                            type="text"
                                            register={register('title', { required: 'Box title is required' })}
                                            name="title"
                                            label="Box Title"
                                            placeholder="Enter box title"
                                        />
                                        <ErrorMessage message={errors.title?.message?.toString()} />
                                    </div>

                                    <div className='mb-3 col-12'>
                                        <label htmlFor="slug" className="form-label">Slug</label>
                                        <input
                                            type="text"
                                            {...register('slug', {
                                                required: 'Slug is required',
                                                pattern: {
                                                    value: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                                                    message: 'Slug must be URL-friendly: lowercase letters, numbers, hyphens. No spaces or special characters.'
                                                }
                                            })}
                                            className='form-control'
                                            name="slug"
                                            placeholder="Enter slug"
                                            onFocus={handleSlugFocus}
                                            onChange={() => setIsSlugTouched(true)}
                                        />
                                        <ErrorMessage message={errors.slug?.message?.toString()} />
                                    </div>

                                    <div className="mb-3 col-12">
                                        <label className="form-label">Description</label>
                                        <textarea
                                            {...register('description')}
                                            className="form-control"
                                            rows={4}
                                            placeholder="Describe this meatery box"
                                        />
                                        <ErrorMessage message={errors.description?.message?.toString()} />
                                    </div>

                                    <div className="mb-3 col-12">
                                        <label className="form-label">Additional Items</label>
                                        <textarea
                                            {...register('additionalItems')}
                                            className="form-control"
                                            rows={4}
                                            placeholder="List any additional items included in the box comma separated (e.g., sauces, spices, recipes)"
                                        />
                                        <ErrorMessage message={errors.additionalItems?.message?.toString()} />
                                    </div>

                                    <div className="mb-3 col-md-4">
                                        <InputField
                                            type="text"
                                            register={register('price', {
                                                required: 'Box price is required',
                                                pattern: {
                                                    value: /^\d+(\.\d{1,2})?$/,
                                                    message: 'Price must be a valid number with up to two decimal places'
                                                }
                                            })}
                                            name="price"
                                            label="Box Price"
                                            placeholder="Enter box price "
                                        />
                                        <ErrorMessage message={errors.price?.message?.toString()} />
                                    </div>

                                    <div className="mb-3 col-12 image-upload">
                                        <div className='form-label fw-bold'>Box Image</div>
                                        <div className="row">
                                            <div className="col-12">
                                                <label htmlFor="file-input-box" className="choosefilenew position-relative">
                                                    <input
                                                        type="file"
                                                        id="file-input-box"
                                                        accept="image/*"
                                                        hidden
                                                        onChange={handleFileChange}
                                                    />
                                                    <div>
                                                        <div className='mb-2'><i className="bi fs-4 bi-upload"></i></div>
                                                        Upload Image
                                                    </div>
                                                </label>
                                            </div>
                                            <div className="col-12 mt-3">
                                                <div className='addimagefiles w-100'>
                                                    {imageURL && (
                                                        <div className="image-card w-100 position-relative">
                                                            <img src={imageURL} alt="Box" className="image-preview w-100" />
                                                            <button
                                                                type="button"
                                                                className='btn btn-danger px-2 py-1 text-white position-absolute'
                                                                style={{ top: 8, right: 8 }}
                                                                onClick={() => handleDeleteImage(imageURL)}
                                                            >
                                                                <i className='bi bi-trash3'></i>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <ErrorMessage message={errors.icon?.message?.toString()} />
                                        <ErrorMessage message={uploadError?.toString()} />
                                        <small className="text-muted">
                                            Maximum file size: 1MB. Supported formats: JPG, PNG, GIF.
                                        </small>
                                    </div>
                                </div>

                                {/* Products Section */}
                                <div className="mt-4">
                                    <h5>Products</h5>
                                    <div className="d-flex gap-2 mb-3">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search products by title"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        <div className="form-check d-flex align-items-center ms-2">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="selectAllProducts"
                                                checked={selectAll}
                                                onChange={(e) => onSelectAllToggle(e.target.checked)}
                                            />
                                            <label className="form-check-label ms-2" htmlFor="selectAllProducts">Select all (filtered)</label>
                                        </div>
                                    </div>
                                    <div className="alert alert-warning mb-3" role="alert">
                                        <strong>Please double-check item quantities before submitting.</strong><br />
                                        Once the bundle is added, item quantities cannot be updated. These quantities are used for
                                        refund calculations, and incorrect quantities may affect refund amounts.
                                    </div>


                                    <div className="table-responsive">
                                        <table className="table table-bordered align-middle">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: 40 }}></th>
                                                    <th>Product</th>
                                                    <th style={{ width: 140 }}>Price</th>
                                                    <th style={{ width: 140 }}>Quantity<br /> <small>(per box)</small> </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredProducts.length === 0 && (
                                                    <tr>
                                                        <td colSpan={4} className="text-center text-muted">No products found</td>
                                                    </tr>
                                                )}
                                                {filteredProducts.map(product => {
                                                    const sel = selectedProducts[product.id];
                                                    const isSelected = !!sel?.selected;
                                                    const price = sel?.price ?? product.productInfo?.[0]?.price ?? '0';
                                                    const qty = sel?.quantity ?? product.productInfo?.[0]?.quantity ?? 1;

                                                    // Safely get image URL
                                                    const productImage = product.images?.[0] || (sel?.product?.images?.[0]) || '/placeholder-image.png';
                                                    const productTitle = product.title || sel?.product?.title || 'Unknown Product';
                                                    const productCode = product.productCode || sel?.product?.productCode || '';

                                                    return (
                                                        <tr key={product.id}>
                                                            <td>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isSelected}
                                                                    onChange={(e) => onProductToggle(product, e.target.checked)}
                                                                />
                                                            </td>
                                                            <td>
                                                                <div className="d-flex align-items-center gap-3">
                                                                    <div style={{ width: 48, height: 48, overflow: 'hidden', borderRadius: 6 }}>
                                                                        <img
                                                                            src={productImage}
                                                                            alt={productTitle}
                                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                            onError={(e) => {
                                                                                (e.target as HTMLImageElement).src = '/placeholder-image.png';
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <div className="fw-semibold">{productTitle}</div>
                                                                        <div className="text-muted small">{productCode}</div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                {price}
                                                                {/* <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={price}
                                                                    onChange={(e) => onChangeSelectedPrice(product.id, e.target.value)}
                                                                    disabled={true}
                                                                /> */}
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className="form-control"
                                                                    min={1}
                                                                    value={qty}
                                                                    onChange={(e) => onChangeSelectedQuantity(product.id, Math.max(1, Number(e.target.value)))}
                                                                    disabled={!isSelected}
                                                                />
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div className="mb-3 col-12 mt-4">
                                    <SeoForm
                                        ref={seoFormRef}
                                        entityId={id || ''}
                                        entityType="bundle"
                                        initialData={seoData}
                                        isRequired={false}
                                    />
                                </div>

                                <div className="d-flex gap-2 justify-content-end mt-4">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary px-4"
                                        onClick={handleCancel}
                                    >
                                        Cancel
                                    </button>
                                    <Button
                                        loading={loadingButton}
                                        className="btn btn-primary text-white px-4"
                                        type="submit"
                                        text={editingBox ? 'Update Box' : 'Save Box'}
                                    />
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default AddMeateryBox;
